<template>
  <div class="mnt-list">
    <div v-if="selectedIds.size >= 2" class="mnt-list__batch-bar">
      <span class="mnt-list__batch-count">{{ selectedIds.size }} tickets seleccionados</span>
      <button class="mnt-list__batch-action" @click="emitBatch('assign-technician')">Asignar técnico</button>
      <button class="mnt-list__batch-action" @click="emitBatch('change-priority')">Cambiar prioridad</button>
      <button class="mnt-list__batch-action" @click="emitBatch('export')">Exportar selección</button>
      <button class="mnt-list__batch-action" @click="emitBatch('mark-resolved')">Marcar como resueltos</button>
      <button class="mnt-list__batch-clear" @click="clearSelection">
        <span class="mdi mdi-close"></span>
      </button>
    </div>

    <div class="mnt-list__col-header">
      <div class="mnt-list__col mnt-list__col--priority"></div>
      <button class="mnt-list__col mnt-list__col--sortable mnt-list__col--code" @click="toggleSort('code')">N° / Tipo</button>
      <button class="mnt-list__col mnt-list__col--sortable mnt-list__col--tenant" @click="toggleSort('tenant')">Nave / Inquilino</button>
      <div class="mnt-list__col mnt-list__col--desc">Título</div>
      <button class="mnt-list__col mnt-list__col--sortable mnt-list__col--tech" @click="toggleSort('tech')">Técnico</button>
      <button class="mnt-list__col mnt-list__col--sortable mnt-list__col--date mnt-list__col--date-created" @click="toggleSort('created')">Reportado</button>
      <button class="mnt-list__col mnt-list__col--sortable mnt-list__col--date mnt-list__col--date-sla" @click="toggleSort('sla')">Fecha límite</button>
      <div class="mnt-list__col mnt-list__col--status">Estatus</div>
      <div class="mnt-list__col mnt-list__col--actions"></div>
    </div>

    <div class="mnt-list__body">
      <div
        v-for="(row, index) in sortedTickets"
        :key="row.id"
        class="mnt-list__row"
        :class="{
          'mnt-list__row--even': index % 2 === 1,
          'mnt-list__row--selected': selectedIds.has(row.id),
          'mnt-list__row--recent': row.recentlyUpdated,
        }"
        @click="onRowClick(row)"
      >
        <div class="mnt-list__col mnt-list__col--priority" @click.stop="toggleSelect(row.id)">
          <template v-if="!selectedIds.size">
            <span class="mnt-list__priority-dot" :style="{ background: priorityColor(row.priority) }"></span>
            <span class="mnt-list__sla-bar" :class="`mnt-list__sla-bar--${row.slaColor}`"></span>
          </template>
          <input v-else type="checkbox" :checked="selectedIds.has(row.id)" @click.stop="toggleSelect(row.id)" />
        </div>

        <div class="mnt-list__col mnt-list__col--code">
          <span class="mnt-list__code">{{ row.code }}</span>
          <span class="mnt-list__type-tag" :class="`mnt-list__type-tag--${row.type}`">{{ typeLabel(row.type) }}</span>
        </div>

        <div
          class="mnt-list__col mnt-list__col--tenant"
          @mouseenter="onTenantHover(row, $event)"
          @mouseleave="hoveredPlotId = null"
        >
          <span class="mnt-list__tenant-name">{{ row.tenantName || 'Sin inquilino' }}</span>
          <span class="mnt-list__plot-tag">{{ row.plotName }}</span>
        </div>

        <div class="mnt-list__col mnt-list__col--desc">
          <span class="mnt-list__desc-text">{{ row.title || '—' }}</span>
          <span v-if="row.photosCount > 0" class="mdi mdi-image-outline mnt-list__desc-photo-icon"></span>
        </div>

        <div
          class="mnt-list__col mnt-list__col--tech"
          @click.stop="row.technician ? null : $emit('assign-technician', row.id)"
          @mouseenter="row.technician && onTechHover(row, $event)"
          @mouseleave="hoveredTechId = null"
        >
          <template v-if="row.technician">
            <span class="mnt-list__tech-avatar" :style="{ background: AVATAR_COLORS[row.technician.avatarColor] || row.technician.avatarColor }">
              {{ initials(row.technician.name) }}
            </span>
            <span class="mnt-list__tech-name">{{ row.technician.name }}</span>
            <span
              v-if="assignmentView(row.assignment)"
              class="mnt-list__assign"
              :style="{ color: assignmentView(row.assignment).color }"
              :title="`${assignmentView(row.assignment).label} · ${assignmentView(row.assignment).detail}`"
            ><span class="mdi" :class="assignmentView(row.assignment).icon"></span></span>
          </template>
          <template v-else>
            <span class="mnt-list__tech-avatar mnt-list__tech-avatar--empty"><span class="mdi mdi-plus"></span></span>
            <span class="mnt-list__tech-unassigned">Sin asignar</span>
          </template>
        </div>

        <div class="mnt-list__col mnt-list__col--date mnt-list__col--date-created">
            <span class="mnt-list__date-main">{{ formatDateShort(row.createdAt) }}</span>
            <span class="mnt-list__date-sub">{{ formatTime(row.createdAt) }}</span>
          </div>

        <div class="mnt-list__col mnt-list__col--date mnt-list__col--date-sla">
          <template v-if="row.slaDueAt">
            <span class="mnt-list__date-main" :class="`mnt-list__date-main--${row.slaColor}`">
              {{ isOverdue(row.slaDueAt) ? 'Vencido' : formatDateShort(row.slaDueAt) }}
            </span>
            <span class="mnt-list__date-sub" :class="isOverdue(row.slaDueAt) ? 'mnt-list__date-sub--red' : ''">
              {{ isOverdue(row.slaDueAt) ? `hace ${daysOverdue(row.slaDueAt)}d` : formatTime(row.slaDueAt) }}
            </span>
          </template>
          <span v-else class="mnt-list__date-sub">—</span>
        </div>

        <div class="mnt-list__col mnt-list__col--status" @click.stop>
          <button class="mnt-list__status-pill" :class="`mnt-list__status-pill--${row.status}`" @click="statusMenuOpenId = statusMenuOpenId === row.id ? null : row.id">
            {{ statusLabel(row.status) }}
          </button>
          <Teleport to="body">
            <div v-if="statusMenuOpenId === row.id" class="mnt-list__status-menu" :style="statusMenuPos">
              <button
                v-for="opt in STATUS_OPTIONS"
                :key="opt.value"
                class="mnt-list__status-option"
                @click="onChangeRowStatus(row, opt.value, $event)"
              >
                {{ opt.label }}
              </button>
            </div>
          </Teleport>
        </div>

        <div class="mnt-list__col mnt-list__col--actions" @click.stop>
          <button class="mnt-list__menu-btn" @click="openRowMenu(row, $event)">
            <span class="mdi mdi-dots-vertical"></span>
          </button>
          <Teleport to="body">
            <div v-if="rowMenuOpenId === row.id" class="mnt-list__row-menu" :style="rowMenuPos">
              <button @click="$emit('assign-technician', row.id); rowMenuOpenId = null">Asignar técnico</button>
              <button @click="$emit('change-priority', row.id); rowMenuOpenId = null">Cambiar prioridad</button>
              <button @click="$emit('open-ticket', row.id); rowMenuOpenId = null">Ver detalle completo</button>
              <button @click="onChangeRowStatus(row, 'RESOLVED'); rowMenuOpenId = null">Marcar como resuelto</button>
              <button @click="$emit('register-cost', row.id); rowMenuOpenId = null">Registrar costo</button>
              <button @click="$emit('cancel-ticket', row.id); rowMenuOpenId = null">Cancelar ticket</button>
            </div>
          </Teleport>
        </div>
      </div>
    </div>

    <div class="mnt-list__footer">
      <div class="mnt-list__footer-left">
        {{ footer.total }} tickets · {{ footer.statusCounts.OPEN }} abiertos · {{ footer.statusCounts.IN_PROGRESS }} en proceso ·
        {{ footer.statusCounts.WAITING }} en espera · {{ footer.statusCounts.RESOLVED }} resueltos
      </div>
      <div class="mnt-list__footer-right">
        <span class="mnt-list__footer-total">{{ formatCurrency(footer.totalCost) }}</span>
        <span class="mnt-list__footer-total">{{ footer.billedPercent }}% cobrado / {{ footer.absorbedPercent }}% absorbido</span>
        <span class="mnt-list__footer-total" v-if="footer.slaCompliancePercent !== null">{{ footer.slaCompliancePercent }}% SLA cumplido</span>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="waitRow" class="mnt-list__wait-backdrop" @click="waitRow = null">
        <div class="mnt-list__wait-modal" @click.stop>
          <h4>Motivo de la espera</h4>
          <select v-model="waitForm.reason" class="mnt-list__wait-input">
            <option value="PARTS">Espera de refacción</option>
            <option value="TENANT_APPROVAL">Espera de aprobación del inquilino</option>
            <option value="EXTERNAL_VENDOR">Espera de proveedor externo</option>
            <option value="OTHER">Otros</option>
          </select>
          <input v-if="waitForm.reason === 'OTHER'" v-model="waitForm.note" placeholder="Describe el motivo" class="mnt-list__wait-input" />
          <div class="mnt-list__wait-actions">
            <button class="mnt-list__wait-btn mnt-list__wait-btn--secondary" @click="waitRow = null">Cancelar</button>
            <button class="mnt-list__wait-btn" @click="confirmWait">Confirmar</button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="hoveredPlotId && plotHistory" class="mnt-list__tooltip" :style="tenantTooltipPos">
        <div class="mnt-list__tooltip-title">Últimos 90 días en esta nave</div>
        <div>{{ plotHistory.totalCount }} tickets</div>
        <div>{{ plotHistory.avgResolutionHours !== null ? `${plotHistory.avgResolutionHours}h promedio resolución` : 'Sin resoluciones recientes' }}</div>
        <div>{{ plotHistory.chargedCount }} con cargo al inquilino</div>
      </div>
      <div v-if="hoveredTechId && hoveredTechInfo" class="mnt-list__tooltip" :style="techTooltipPos">
        <div class="mnt-list__tooltip-title">{{ hoveredTechInfo.name }}</div>
        <div>{{ hoveredTechInfo.activeTicketCount }} tickets activos</div>
        <div>{{ hoveredTechInfo.avgResolutionHours !== null ? `${hoveredTechInfo.avgResolutionHours}h prom. resolución` : 'Sin datos recientes' }}</div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { getTicketsList, getPlotTicketHistory, updateTicketStatus } from '@/services/maintenance'
import { assignmentView } from './assignmentUi'

const props = defineProps({
  parkId: { type: String, required: true },
  search: { type: String, default: '' },
  typeFilters: { type: Array, default: () => [] },
  technicianFilters: { type: Array, default: () => [] },
  technicians: { type: Array, default: () => [] },
  statusFilter: { type: String, default: 'todos' },
})

const emit = defineEmits(['open-ticket', 'assign-technician', 'change-priority', 'register-cost', 'cancel-ticket', 'batch-action', 'tickets-updated'])
const AVATAR_COLORS = {
  blue: '#3b82f6', green: '#10b981', violet: '#8b5cf6', orange: '#f97316',
  teal: '#14b8a6', pink: '#ec4899', indigo: '#6366f1', amber: '#f0a500',
}

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Abierto' },
  { value: 'IN_PROGRESS', label: 'En proceso' },
  { value: 'WAITING', label: 'En espera' },
  { value: 'RESOLVED', label: 'Resuelto' },
]

const tickets = ref([])
const footer = ref({ total: 0, statusCounts: { OPEN: 0, IN_PROGRESS: 0, WAITING: 0, RESOLVED: 0 }, totalCost: 0, billedPercent: 0, absorbedPercent: 0, slaCompliancePercent: null })

async function loadList() {
  const result = await getTicketsList(props.parkId, {
    search: props.search,
    types: props.typeFilters,
    technicianIds: props.technicianFilters,
  })
  tickets.value = result.tickets
  footer.value = result.footer
  emit('tickets-updated', tickets.value)
}

let pollHandle = null
onMounted(() => {
  loadList()
  pollHandle = setInterval(loadList, 8000)
})
onUnmounted(() => clearInterval(pollHandle))

watch([() => props.search, () => props.typeFilters, () => props.technicianFilters], loadList, { deep: true })

// --- Orden ---
const sortKey = ref(null)
const sortDir = ref('asc')

function toggleSort(key) {
  if (sortKey.value !== key) {
    sortKey.value = key
    sortDir.value = 'asc'
  } else if (sortDir.value === 'asc') {
    sortDir.value = 'desc'
  } else {
    sortKey.value = null
  }
}

function sortValue(row, key) {
  if (key === 'code') return row.code
  if (key === 'tenant') return row.tenantName || ''
  if (key === 'tech') return row.technician?.name || ''
  if (key === 'created') return new Date(row.createdAt).getTime()
  if (key === 'sla') return row.slaDueAt ? new Date(row.slaDueAt).getTime() : Infinity
  return ''
}

const sortedTickets = computed(() => {
  let list = tickets.value
  if (props.statusFilter !== 'todos') {
    const targetStatus = STATUS_TAB_MAP[props.statusFilter]
    list = list.filter((t) => t.status === targetStatus)
  }

  const unassigned = list.filter((t) => !t.technician)
  const assigned = list.filter((t) => t.technician)

  if (!sortKey.value) return [...unassigned, ...assigned]

  const sortFn = (a, b) => {
    const va = sortValue(a, sortKey.value)
    const vb = sortValue(b, sortKey.value)
    if (va < vb) return sortDir.value === 'asc' ? -1 : 1
    if (va > vb) return sortDir.value === 'asc' ? 1 : -1
    return 0
  }

  return [...[...unassigned].sort(sortFn), ...[...assigned].sort(sortFn)]
})

// --- Selección múltiple ---
const selectedIds = ref(new Set())

function toggleSelect(id) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}

function clearSelection() {
  selectedIds.value = new Set()
}

function emitBatch(action) {
  emit('batch-action', { action, ids: [...selectedIds.value] })
}

function onRowClick(row) {
  if (selectedIds.value.size > 0) {
    toggleSelect(row.id)
    return
  }
  emit('open-ticket', row.id)
}

// --- Estatus editable ---
const statusMenuOpenId = ref(null)
const statusMenuPos = ref({ top: '0px', left: '0px' })

const waitRow = ref(null)
const waitForm = ref({ reason: 'PARTS', note: '' })

async function confirmWait() {
  const row = waitRow.value
  waitRow.value = null
  try {
    await updateTicketStatus(row.id, { status: 'WAITING', waitReason: waitForm.value.reason, waitReasonNote: waitForm.value.note })
    await loadList()
  } catch (error) {
    alert(error.message)
  }
}

async function onChangeRowStatus(row, newStatus) {
  statusMenuOpenId.value = null
  // Poner un ticket en espera exige decir por qué.
  if (newStatus === 'WAITING') {
    waitForm.value = { reason: 'PARTS', note: '' }
    waitRow.value = row
    return
  }
  try {
    await updateTicketStatus(row.id, { status: newStatus })
    await loadList()
  } catch (error) {
    alert(error.message)
  }
}

// --- Menú de acciones ---
const rowMenuOpenId = ref(null)
const rowMenuPos = ref({ top: '0px', left: '0px' })

function openRowMenu(row, event) {
  if (rowMenuOpenId.value === row.id) {
    rowMenuOpenId.value = null
    return
  }
  const rect = event.currentTarget.getBoundingClientRect()
  rowMenuPos.value = { top: rect.top - 4 + 'px', left: rect.right - 200 + 'px' }
  rowMenuOpenId.value = row.id
}

function handleOutsideClick(e) {
  if (!e.target.closest('.mnt-list__menu-btn') && !e.target.closest('.mnt-list__row-menu')) rowMenuOpenId.value = null
  if (!e.target.closest('.mnt-list__status-pill') && !e.target.closest('.mnt-list__status-menu')) statusMenuOpenId.value = null
}
onMounted(() => window.addEventListener('click', handleOutsideClick))
onUnmounted(() => window.removeEventListener('click', handleOutsideClick))

const STATUS_TAB_MAP = { abiertos: 'OPEN', enProceso: 'IN_PROGRESS', enEspera: 'WAITING', resueltos: 'RESOLVED' }

// --- Tooltips ---
const hoveredPlotId = ref(null)
const plotHistory = ref(null)
const tenantTooltipPos = ref({ top: '0px', left: '0px' })

async function onTenantHover(row, event) {
  hoveredPlotId.value = row.plotId
  const rect = event.currentTarget.getBoundingClientRect()
  tenantTooltipPos.value = { top: rect.bottom + 6 + 'px', left: rect.left + 'px' }
  plotHistory.value = await getPlotTicketHistory(row.plotId)
}

const hoveredTechId = ref(null)
const techTooltipPos = ref({ top: '0px', left: '0px' })

const hoveredTechInfo = computed(() => props.technicians.find((t) => t.id === hoveredTechId.value) || null)

function onTechHover(row, event) {
  hoveredTechId.value = row.technician.id
  const rect = event.currentTarget.getBoundingClientRect()
  techTooltipPos.value = { top: rect.bottom + 6 + 'px', left: rect.left + 'px' }
}

// --- Formato ---
function priorityColor(p) {
  return { CRITICAL: '#e84545', HIGH: '#f97316', MEDIUM: '#f0a500', LOW: '#94a3b8' }[p] || '#94a3b8'
}

function typeLabel(t) {
  return { CORRECTIVE: 'Correctivo', PREVENTIVE: 'Preventivo', IMPROVEMENT: 'Mejora' }[t] || t
}

function statusLabel(s) {
  return STATUS_OPTIONS.find((o) => o.value === s)?.label || s
}

function initials(name) {
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
}

function formatDateShort(iso) {
  const d = new Date(iso)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
  const isYesterday = d.toDateString() === yesterday.toDateString()
  if (isToday) return 'Hoy'
  if (isYesterday) return 'Ayer'
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
}

function isOverdue(slaDueAt) {
  return new Date(slaDueAt) < new Date()
}

function daysOverdue(slaDueAt) {
  return Math.floor((Date.now() - new Date(slaDueAt).getTime()) / (24 * 60 * 60 * 1000))
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}
</script>

<style scoped>
.mnt-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem;
  container-type: inline-size;
  container-name: mnt-list;
}

@container mnt-list (max-width: 900px) {
  .mnt-list .mnt-list__col-header,
  .mnt-list .mnt-list__row {
    grid-template-columns: 30px 70px minmax(0, 20fr) minmax(0, 20fr) minmax(0, 14fr) minmax(0, 12fr) minmax(0, 14fr) 32px;
  }
  .mnt-list .mnt-list__col--date-created {
    display: none;
  }
}

@container mnt-list (max-width: 700px) {
  .mnt-list .mnt-list__col-header,
  .mnt-list .mnt-list__row {
    grid-template-columns: 28px minmax(0, 22fr) minmax(0, 22fr) minmax(0, 16fr) minmax(0, 14fr) minmax(0, 16fr) 32px;
  }
  .mnt-list .mnt-list__col--code {
    display: none;
  }
}

.mnt-list__batch-bar {
  height: 44px;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem;
  background: #3b82f6;
  flex-shrink: 0;
}

.mnt-list__batch-count {
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
}

.mnt-list__batch-action {
  background: none;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  padding-left: 1rem;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
}

.mnt-list__batch-clear {
  margin-left: auto;
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
}

.mnt-list__col-header {
  display: grid;
  grid-template-columns: 36px 100px minmax(0, 20fr) minmax(0, 25fr) 120px 90px 90px 100px 48px;
  height: 36px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.mnt-list__col--sortable {
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-body);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
}

.mnt-list__col--desc {
  font-family: var(--font-body);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
}

.mnt-list__col--status {
  font-family: var(--font-body);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
}

.mnt-list__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.mnt-list__body::-webkit-scrollbar { width: 6px; }
.mnt-list__body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.mnt-list__body::-webkit-scrollbar-track { background: transparent; }

.mnt-list__row {
  display: grid;
  grid-template-columns: 36px 100px minmax(0, 20fr) minmax(0, 25fr) 120px 90px 90px 100px 48px;
  height: 52px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  cursor: pointer;
  transition: background 80ms ease;
}

.mnt-list__row--even { background: #fafafa; }
.mnt-list__row:hover { background: #eff6ff; }
.mnt-list__row--selected { background: #eff6ff; }
.mnt-list__row--recent { border-left: 2px solid #3b82f6; }

.mnt-list__col {
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  min-width: 0;
  position: relative;
  overflow: hidden;
}

.mnt-list__col--priority {
  flex-direction: column;
  gap: 3px;
  justify-content: center;
}

.mnt-list__priority-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.mnt-list__row:hover .mnt-list__priority-dot { width: 10px; height: 10px; }

.mnt-list__sla-bar {
  width: 20px;
  height: 3px;
  border-radius: 2px;
}

.mnt-list__sla-bar--green { background: #10b981; }
.mnt-list__sla-bar--amber { background: #f0a500; }
.mnt-list__sla-bar--red { background: #e84545; }

.mnt-list__col--code {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  min-width: 0;
}

.mnt-list__code {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
}

.mnt-list__type-tag {
  font-size: 9px;
  border-radius: 4px;
  padding: 0.05rem 0.3rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  box-sizing: border-box;
  display: inline-block;
}

.mnt-list__type-tag--CORRECTIVE { background: #fee2e2; color: #dc2626; }
.mnt-list__type-tag--PREVENTIVE { background: #dbeafe; color: #1d4ed8; }
.mnt-list__type-tag--IMPROVEMENT { background: #ede9fe; color: #6d28d9; }

.mnt-list__col--tenant {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
}

.mnt-list__tenant-name {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  width: 100%;
  min-width: 0;
}

.mnt-list__plot-tag {
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
}

.mnt-list__desc-text {
  text-transform: none; /* la columna hereda mayúsculas del encabezado; el título se muestra tal cual se escribió */
  letter-spacing: normal;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
}

.mnt-list__desc-photo-icon {
  color: #94a3b8;
  font-size: 12px;
  flex-shrink: 0;
  margin-left: 0.3rem;
}

.mnt-list__col--tech {
  gap: 0.4rem;
  cursor: pointer;
  min-width: 0;
}

.mnt-list__tech-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 9px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mnt-list__tech-avatar--empty {
  border: 1px dashed #cbd5e1;
  color: #94a3b8;
}

.mnt-list__assign {
  flex-shrink: 0;
  font-size: 15px;
  line-height: 1;
}

.mnt-list__tech-name {
  font-family: var(--font-body);
  font-size: 12px;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
}

.mnt-list__tech-unassigned {
  font-family: var(--font-body);
  font-size: 12px;
  color: #dc2626;
}

.mnt-list__col--date {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1px;
}

.mnt-list__date-main {
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
}

.mnt-list__date-main--navy { color: #1e3a5f; }
.mnt-list__date-main--amber { color: #f0a500; }
.mnt-list__date-main--red { color: #e84545; }

.mnt-list__date-sub {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
}

.mnt-list__date-sub--red { color: #dc2626; }

.mnt-list__status-pill {
  border-radius: 20px;
  padding: 0.2rem 0.6rem;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 200ms ease;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mnt-list__status-pill--OPEN { background: #dbeafe; color: #2563eb; }
.mnt-list__status-pill--IN_PROGRESS { background: #fef3c7; color: #d97706; }
.mnt-list__status-pill--WAITING { background: #ffedd5; color: #ea580c; }
.mnt-list__status-pill--RESOLVED { background: #dcfce7; color: #16a34a; }

.mnt-list__wait-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center; z-index: 1100; }
.mnt-list__wait-modal { background: #fff; border-radius: 10px; padding: 1.25rem; min-width: 320px; display: flex; flex-direction: column; gap: 0.6rem; }
.mnt-list__wait-modal h4 { margin: 0; font-family: var(--font-display); font-size: 15px; color: #1e3a5f; }
.mnt-list__wait-input { font-family: var(--font-body); font-size: 13px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.5rem 0.6rem; color: #1e3a5f; }
.mnt-list__wait-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.4rem; }
.mnt-list__wait-btn { background: #3b82f6; color: #fff; border: none; border-radius: 6px; padding: 0.5rem 0.9rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; }
.mnt-list__wait-btn--secondary { background: none; border: 1px solid #e2e8f0; color: #64748b; }

.mnt-list__status-menu,
.mnt-list__row-menu {
  position: fixed;
  transform: translateY(-100%);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  min-width: 180px;
  z-index: 1000;
  padding: 0.3rem;
}

.mnt-list__status-option,
.mnt-list__row-menu button {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  border-radius: 5px;
}

.mnt-list__status-option:hover,
.mnt-list__row-menu button:hover {
  background: #f8fafc;
}

.mnt-list__col--actions {
  justify-content: center;
}

.mnt-list__menu-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.mnt-list__footer {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  background: #f8f9fc;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.mnt-list__footer-left {
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
}

.mnt-list__footer-right {
  display: flex;
  gap: 1rem;
}

.mnt-list__footer-total {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #1e3a5f;
}

.mnt-list__tooltip {
  position: fixed;
  background: #1e3a5f;
  color: #ffffff;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font-family: var(--font-body);
  font-size: 11px;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  min-width: 180px;
  line-height: 1.5;
}

.mnt-list__tooltip-title {
  font-weight: 600;
  margin-bottom: 0.2rem;
}
</style>